import { Bell, CheckCheck, Code2, Lock, Plug, ShieldCheck, Sparkles, Target, type LucideIcon } from "lucide-react";
import type { MouseEvent } from "react";
import { PillButton, SplitHeader } from "./SitePrimitives";

const ICONS: Record<string, LucideIcon> = {
  focus: Target,
  clean: CheckCheck,
  shield: ShieldCheck,
  sparkles: Sparkles,
  code: Code2,
  plug: Plug,
  bell: Bell,
  lock: Lock,
};

export function SolutionIcon({ name, className = "h-5 w-5" }: { name?: string; className?: string }) {
  const Icon = ICONS[name || ""] || Sparkles;
  return (
    <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#132626] text-[var(--public-accent)]">
      <Icon className={className} aria-hidden="true" />
    </span>
  );
}

export function Tag({ children, live = false }: { children: string; live?: boolean }) {
  if (!children) return null;
  return (
    <span
      className={`rounded-full border px-[10px] py-1 font-['Inter'] text-[12px] font-semibold leading-none ${
        live
          ? "border-[var(--public-accent)] bg-[var(--public-accent)] text-[var(--public-cta-ink)]"
          : "border-[#2B4040] text-[var(--public-muted)]"
      }`}
    >
      {children}
    </span>
  );
}

/** iPhone-style frame around an App Store screenshot. */
export function PhoneShot({ src, className = "" }: { src: string; className?: string }) {
  return (
    <div className={`overflow-hidden rounded-[30px] border-[6px] border-[#1F2A2A] bg-white shadow-[0_20px_40px_rgba(0,0,0,0.45)] ${className}`}>
      <img src={src} alt="" loading="lazy" className="block aspect-[458/996] w-full object-cover object-top" />
    </div>
  );
}

export type FeaturedSolution = {
  name: string;
  href: string;
  summary: string;
  icon?: string;
  image?: string;
  tags: string[];
  statusLabel: string;
  appStoreUrl?: string;
};

export type SolutionLineCard = { title: string; description: string; linkLabel: string; href: string; icon?: string };

/** Home page section: the featured solution plus the other lines of work, linking to /solutions. */
export function SiteSolutions({
  badge,
  title,
  description,
  featured,
  featuredCta,
  appStoreLabel,
  lines,
  ctaLabel,
  ctaHref,
  onNavigate,
}: {
  badge: string;
  title: string;
  description: string;
  featured?: FeaturedSolution;
  featuredCta: string;
  appStoreLabel: string;
  lines: SolutionLineCard[];
  ctaLabel: string;
  ctaHref: string;
  onNavigate: (href: string) => void;
}) {
  if (!title && !featured) return null;
  const link = (href: string) => (event: MouseEvent) => {
    if (href.startsWith("#")) {
      event.preventDefault();
      onNavigate(href);
    }
  };
  return (
    <section id="solutions" className="pt-[80px] min-[810px]:pt-[100px] min-[1200px]:pt-[120px]">
      <div className="site-container">
        <SplitHeader badge={badge} title={title} description={description} align="end" titleClassName="min-[1200px]:max-w-[520px]" />
        <div className="mt-10 grid gap-6 min-[810px]:mt-12 min-[1200px]:mt-14 min-[1200px]:grid-cols-[1.35fr_1fr]">
          {featured ? (
            <article className="relative grid gap-7 overflow-hidden rounded-[20px] border border-[#1D3A39] bg-[linear-gradient(135deg,#0F2827_0%,#0D1A1A_60%)] p-6 min-[810px]:grid-cols-[1fr_210px] min-[810px]:p-9">
              <div>
                {featured.icon ? <img src={featured.icon} alt="" className="h-16 w-16 rounded-[15px]" /> : null}
                <div className="my-4 flex flex-wrap gap-2">
                  <Tag live>{featured.statusLabel}</Tag>
                  {featured.tags.map((tag) => (
                    <Tag key={tag}>{tag}</Tag>
                  ))}
                </div>
                <h3 className="text-[30px] font-bold leading-[1.15] text-white">{featured.name}</h3>
                {featured.summary ? <p className="site-lead mb-6 mt-3 min-[1200px]:max-w-[420px]">{featured.summary}</p> : null}
                <div className="flex flex-wrap gap-3">
                  <PillButton href={featured.href} size="sm">
                    {featuredCta} {featured.name}
                  </PillButton>
                  {featured.appStoreUrl ? (
                    <a
                      href={featured.appStoreUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center rounded-xl border border-[#2E3B3B] px-4 py-[10px] text-[16px] font-semibold text-white transition-colors hover:border-[var(--public-accent)]"
                    >
                      {appStoreLabel}
                    </a>
                  ) : null}
                </div>
              </div>
              {featured.image ? (
                <PhoneShot src={featured.image} className="mx-auto -mb-[90px] w-[210px] self-end min-[810px]:-mb-[60px]" />
              ) : null}
            </article>
          ) : null}
          {lines.length ? (
            <div className="grid gap-6 min-[810px]:grid-cols-2 min-[1200px]:grid-cols-1">
              {lines.map((line) => (
                <article
                  key={line.title}
                  className="flex flex-col justify-between gap-4 rounded-[20px] border border-[#1C2626] bg-[#111818] p-7"
                >
                  <SolutionIcon name={line.icon} />
                  <div>
                    <h3 className="text-[20px] font-bold leading-[1.2] text-white">{line.title}</h3>
                    {line.description ? <p className="site-body mt-2 text-white/70 min-[810px]:text-[14px]">{line.description}</p> : null}
                    {line.linkLabel ? (
                      <a
                        href={line.href}
                        onClick={link(line.href)}
                        className="mt-3 inline-block font-['Inter'] text-[14px] font-semibold text-[var(--public-accent)] hover:underline"
                      >
                        {line.linkLabel} →
                      </a>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          ) : null}
        </div>
        {ctaLabel ? (
          <div className="mt-11 flex justify-center">
            <PillButton href={ctaHref}>{ctaLabel}</PillButton>
          </div>
        ) : null}
      </div>
    </section>
  );
}
